import React, { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination, Keyboard, A11y } from 'swiper/modules';
import { motion, useReducedMotion, AnimatePresence } from 'framer-motion';
import type { HeroBannerProps, HeroSlide } from './types';

const HeroBanner: React.FC<HeroBannerProps> = ({
  data,
  onCtaClick,
  className = '',
}) => {
  const reduceMotion = useReducedMotion();
  const motionSafe = !reduceMotion;
  const [activeIndex, setActiveIndex] = useState(0);
  const [swiperInstance, setSwiperInstance] = useState<any>(null);

  if (!data || !data.slides || data.slides.length === 0) {
    throw new Error('HeroBanner requires data with slides');
  }

  const { slides, imageSlideInterval } = data;
  const isCarousel = slides.length > 1;

  const handleVideoEnd = () => {
    if (swiperInstance && isCarousel) {
      swiperInstance.slideNext();
    }
  };

  const renderMedia = (slide: HeroSlide, isActive: boolean) => {
    if (slide.backgroundVideo) {
      return (
        <video
          src={slide.backgroundVideo}
          poster={slide.backgroundImage}
          preload="auto"
          autoPlay={isActive}
          muted
          playsInline
          loop={false}
          onEnded={handleVideoEnd}
          className="h-full w-full object-cover"
          style={{ opacity: 1 }}
        />
      );
    }

    return (
      <img
        src={slide.backgroundImage}
        alt={slide.altText}
        loading="eager"
        fetchPriority="high"
        sizes="100vw"
        className="h-full w-full object-cover"
      />
    );
  };

  const renderSlide = (slide: HeroSlide, index: number) => {
    const isActive = index === activeIndex;

    return (
      <SwiperSlide key={slide.id} className="!w-full relative">
        {/* Background Media with smooth Ken Burns effect */}
        <motion.div
          key={`${slide.id}-media`}
          className="absolute inset-0 overflow-hidden will-change-transform"
          initial={{ opacity: 0, scale: 1 }}
          animate={{
            opacity: isActive ? 1 : 0,
            scale: isActive ? 1.1 : 1,
            y: isActive ? 0 : 10,
          }}
          transition={{
            opacity: { duration: 0.8 },
            scale: { duration: 12, ease: 'linear' },
            y: { duration: 12, ease: 'linear' },
          }}
        >
          {renderMedia(slide, isActive)}

          {/* Smooth animated overlay */}
          <motion.div
            className="absolute inset-0 bg-black/40"
            initial={{ opacity: 0.2 }}
            animate={{ opacity: isActive ? 1 : 0.2 }}
            transition={{ duration: 1 }}
          />
        </motion.div>

        {/* Content with AnimatePresence for smooth slide transitions */}
        <div className="relative z-10 flex h-full min-h-[100svh] md:min-h-screen flex-col items-center justify-center px-4 text-center sm:px-6 lg:px-8">
          <AnimatePresence mode="wait">
            {isActive && (
              <motion.div
                key={activeIndex}
                className="flex flex-col items-center"
                initial={motionSafe ? { opacity: 0, y: 40, scale: 0.98 } : { opacity: 0 }}
                animate={motionSafe ? { opacity: 1, y: 0, scale: 1 } : { opacity: 1 }}
                exit={motionSafe ? { opacity: 0, y: 40, scale: 0.98 } : { opacity: 0 }}
                transition={motionSafe ? {
                  duration: 0.8,
                  ease: [0.16, 1, 0.3, 1],
                } : { duration: 0.5 }}
              >
                {/* Screen reader announcement */}
                <div className="sr-only" aria-live="polite" aria-atomic="true">
                  {slide.title}
                </div>

                {/* Subtitle */}
                <motion.p
                  className="mb-3 max-w-[90vw] sm:max-w-xl text-white/80 text-xs sm:text-sm md:text-base lg:text-lg tracking-wide mx-auto"
                  initial={motionSafe ? { opacity: 0, y: 20, scale: 0.95 } : {}}
                  animate={motionSafe ? { opacity: 1, y: 0, scale: 1 } : {}}
                  transition={motionSafe ? { duration: 0.6, delay: 0.1 } : {}}
                >
                  {slide.subtitle}
                </motion.p>

                {/* Title */}
                <motion.h1
                  className="font-['Playfair_Display',serif] font-bold text-white leading-[1.1] text-balance text-[clamp(2.5rem,8vw,7rem)] sm:text-[clamp(3rem,8vw,7rem)] mb-6 sm:mb-8"
                  initial={motionSafe ? { opacity: 0, y: 20, scale: 0.95 } : {}}
                  animate={motionSafe ? { opacity: 1, y: 0, scale: 1 } : {}}
                  transition={motionSafe ? { duration: 0.6, delay: 0.2 } : {}}
                >
                  {slide.title}
                </motion.h1>

                {/* CTA with phone icon, "Book Now" text and number */}
                <motion.button
                  onClick={() => onCtaClick?.(slide, index)}
                  className="relative inline-flex items-center justify-center gap-3 rounded-lg bg-primary px-6 py-3 sm:px-8 sm:py-4 text-xs sm:text-sm font-bold text-white transition-colors hover:bg-rose-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 font-['Inter',sans-serif]"
                  initial={motionSafe ? { opacity: 0, y: 20, scale: 0.95 } : {}}
                  animate={motionSafe ? { opacity: 1, y: 0, scale: 1 } : {}}
                  transition={motionSafe ? { duration: 0.6, delay: 0.3 } : {}}
                  whileHover={motionSafe ? {
                    scale: 1.05,
                    boxShadow: '0 20px 40px rgba(244, 63, 94, 0.4)'
                  } : undefined}
                  whileTap={motionSafe ? { scale: 0.95 } : undefined}
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span>Book Your Appointment</span>
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </SwiperSlide>
    );
  };

  return (
    <section
      role="region"
      aria-roledescription="carousel"
      aria-label="Hero banner"
      className={`relative min-h-[100svh] md:min-h-screen w-screen left-1/2 -translate-x-1/2 overflow-hidden hero-banner ${className}`}
    >
      <Swiper
        modules={[Autoplay, Navigation, Pagination, Keyboard, A11y]}
        slidesPerView={1}
        spaceBetween={0}
        loop={isCarousel}
        speed={1000}
        onSwiper={(swiper) => {
          setSwiperInstance(swiper);
          setActiveIndex(swiper.realIndex);
        }}
        onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
        observer={true}
        observeParents={true}
        keyboard={{
          enabled: isCarousel,
          onlyInViewport: true,
        }}
        navigation={{
          enabled: isCarousel,
          prevEl: '.swiper-button-prev',
          nextEl: '.swiper-button-next',
        }}
        pagination={isCarousel ? {
          clickable: true,
          bulletClass: 'swiper-pagination-bullet !bg-white/50 !w-2 !h-2 !rounded-full',
          bulletActiveClass: 'swiper-pagination-bullet-active !w-8 !bg-white',
        } : false}
        autoplay={isCarousel ? {
          delay: imageSlideInterval,
          pauseOnMouseEnter: true,
          disableOnInteraction: false,
        } : false}
        a11y={{
          enabled: true,
          prevSlideMessage: 'Previous slide',
          nextSlideMessage: 'Next slide',
          firstSlideMessage: 'This is the first slide',
          lastSlideMessage: 'This is the last slide',
        }}
        className="h-full !min-h-[100svh] md:!min-h-screen"
      >
        {slides.map((slide, index) => renderSlide(slide, index))}
      </Swiper>
    </section>
  );
};

export default HeroBanner;
